import React from "react";
import { Navigate } from "react-router-dom";
import PrivacyV12Document from "./documents/PrivacyV12Document";
import BiztingPrivacyPolicyV13Page from "./BiztingPrivacyPolicyV13Page";
import {
  PRIVACY_V12_EFFECTIVE_AT,
  PRIVACY_V12_NOTICE_START_AT,
  PRIVACY_V13_EFFECTIVE_AT,
  PRIVACY_V13_NOTICE_START_AT,
  getCurrentBiztingPrivacyRoute,
  getScheduledBiztingPrivacyRoute,
} from "./clauseVersionDates";

type BiztingPrivacyPolicyV12PageProps = {
  forceCurrent?: boolean;
};

const BiztingPrivacyPolicyV12Page = ({ forceCurrent = false }: BiztingPrivacyPolicyV12PageProps) => {
  const now = new Date();
  const isV12Effective = now >= PRIVACY_V12_EFFECTIVE_AT;
  const isV13NoticeStarted = now >= PRIVACY_V13_NOTICE_START_AT;
  const isV13Effective = now >= PRIVACY_V13_EFFECTIVE_AT;

  if (!forceCurrent && (isV13Effective || isV13NoticeStarted)) {
    return <BiztingPrivacyPolicyV13Page />;
  }

  if (now < PRIVACY_V12_NOTICE_START_AT) {
    return <Navigate replace to="/clause/privacy" />;
  }

  return (
    <PrivacyV12Document
      mode={isV13Effective ? "previous" : isV12Effective ? "current" : "scheduled"}
      currentVersionRoute={getCurrentBiztingPrivacyRoute(now)}
      scheduledVersionRoute={getScheduledBiztingPrivacyRoute(now)}
      modal={isV12Effective ? undefined : {
        title: "이전 버전 대비 변경사항 요약",
        subtitle: "2026년 5월 1일 시행 버전 → 2026년 6월 5일 시행 버전",
        ctaLabel: "현재 버전 확인하기",
        ctaRoute: "/clause/privacy/v1.1",
        content: (
          <>
            <div className="tw-mb-6">
              <div className="tw-mb-3 tw-flex tw-items-center tw-gap-2 tw-text-[13.5px] tw-font-bold tw-text-[#081f4a]">
                <span className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded tw-bg-[#1a6ef5] tw-text-[11px] tw-font-bold tw-text-white">1</span>
                개인정보처리방침 변경 내용
              </div>
              <div className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-slate-200">
                <table className="tw-w-full tw-border-collapse tw-text-left tw-text-[13px]">
                  <thead>
                  <tr className="tw-bg-[#081f4a] tw-text-white">
                    <th className="tw-w-[30%] tw-px-3 tw-py-2.5 tw-font-semibold">조항</th>
                    <th className="tw-px-3 tw-py-2.5 tw-font-semibold">변경 내용</th>
                  </tr>
                  </thead>
                  <tbody>
                  {[
                    ["제1조", "제3자 제공", "제3자 제공 조항 신설 — 이름, 연락처, 이메일 제공 / 마케팅 목적"],
                    ["제5조", "국외 이전", "국외 이전 조항 신설 — 현재 국외 이전 없음, 향후 이전 시 고지 의무 명시"],
                    ["제3조", "개인정보 처리 업무의 위탁", "알림톡 및 메시지 발송을 위한 위탁 내용 추가, 카드번호 및 무통장입금 계좌번호 암호화 조치"],
                    ["제7조", "안전성 확보조치", "MFA, 암호화, 로그 보관 등 보안 조치 강화"],
                    ["제8조", "정보주체 권리", "자동화된 의사결정 없음 명시, 위임장 고시번호 제2025-5호로 업데이트"],
                  ].map(([article, label, description], index) => (
                    <tr key={article} className={`tw-border-t tw-border-slate-200 ${index % 2 === 1 ? "tw-bg-slate-50" : ""}`}>
                      <td className="tw-px-3 tw-py-2.5 tw-align-top tw-font-semibold">
                        {article}
                        <br/>
                        <span className="tw-text-[12px] tw-font-normal tw-text-slate-500">{label}</span>
                      </td>
                      <td className="tw-px-3 tw-py-2.5">{description}</td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <div className="tw-mb-3 tw-flex tw-items-center tw-gap-2 tw-text-[13.5px] tw-font-bold tw-text-[#081f4a]">
                <span className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded tw-bg-[#1a6ef5] tw-text-[11px] tw-font-bold tw-text-white">2</span>
                고객사 주요 준수 사항 (N-Pass 이용 시)
              </div>
              <ul className="tw-list-none">
                {[
                  "관리자 계정 2단계 인증(MFA) 필수 설정 및 접근 권한 최소화",
                  "개인정보 목적 외 사용 금지 및 재제공·판매 금지",
                  "다운로드 파일 암호화 및 사유 기록 의무",
                  "파기 요청 시 5일 이내 복구 불가능한 방법으로 파기",
                  "서비스 종료 시 보유 개인정보 5일 내 전량 파기",
                  "위반 시 발생하는 모든 법적 책임은 고객사 부담",
                ].map((item) => (
                  <li key={item} className="tw-flex tw-gap-2 tw-py-2 tw-text-[13.5px] tw-leading-7 tw-text-slate-600">
                    <span className="tw-text-[#1a6ef5]">▸</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </>
        ),
      }}
    />
  );
};

export default BiztingPrivacyPolicyV12Page;
